import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, CheckCircle2, Home, Loader2, Phone } from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import SeoHead from "@/components/SeoHead";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import { useBrand, useMarket, useTenantPath } from "@/lib/brand";
import { currencySymbol } from "@/lib/terms";
import SalesMap from "@/components/SalesMap";

const PROPERTY_TYPES = ["Detached", "Semi-Detached", "Terraced", "Bungalow", "Flat/Apartment"];
const US_PROPERTY_TYPES = ["Single-family", "Townhouse", "Condo", "Multi-family", "Apartment"];
const CONDITIONS = ["Needs work", "Fair", "Good", "Excellent", "New build"];
const TENURES = ["Freehold", "Leasehold", "Share of Freehold"];
const FEATURES = ["Garden", "Garage", "Parking", "Conservatory", "Fireplace"];
const US_FEATURES = ["Yard", "Garage", "Pool", "Basement", "Fireplace"];
// Must match the ImprovementType enum in the backend exactly — the API rejects
// anything else.
const IMPROVEMENT_TYPES = [
  "Extension", "Loft conversion", "Garage conversion", "Conservatory",
  "New kitchen", "New bathroom", "Full refurbishment", "Windows/roof",
  "Garden/landscaping", "Annexe/outbuilding", "Solar/heat pump", "Other",
];
// The types where a floor-area figure means anything. Mirrors the backend,
// which ignores sq ft on everything else — no point asking for a number we
// then throw away.
const SPACE_ADDING_TYPES = [
  "Extension", "Loft conversion", "Garage conversion", "Conservatory",
  "Annexe/outbuilding",
];
const MAX_IMPROVEMENTS = 12;

const STEPS = [
  { n: 1, label: "Property" },
  { n: 2, label: "Your details" },
  { n: 3, label: "Your report" },
];

// Error key → the id of the control we scroll to and focus. The address block
// renders either the lookup Select or the manual inputs, never both, so the
// two share a single id.
const FIELD_IDS = {
  postcode: "postcode",
  address: "address",
  property_type: "property-type",
  bedrooms: "bedrooms",
  bathrooms: "bathrooms",
  size_sqft: "size",
  improvements: "improvements",
  full_name: "name",
  email: "email",
  phone: "phone",
  consent: "consent",
};

const initial = {
  intent: "sale",
  postcode: "",
  property_type: "Detached",
  bedrooms: 3,
  bathrooms: 2,
  size_sqft: 1400,
  condition: "Good",
  tenure: "Freehold",
  year_built: "",
  features: [],
  improvements: [],
  full_name: "",
  email: "",
  phone: "",
  house_id: "",
  street: "",
  selected_address_display: "",
  address_line2: "",
  town: "",
  owner_purchase_price: "",
  owner_purchase_year: "",
  consent: false,
};

export default function Valuation() {
  const navigate = useNavigate();
  const brand = useBrand();
  const tenantPath = useTenantPath();
  const market = useMarket();
  const isUs = market === "us";
  const cur = currencySymbol(market);
  const rentSuffix = isUs ? "/mo" : " pcm";
  const propertyTypes = isUs ? US_PROPERTY_TYPES : PROPERTY_TYPES;
  const features = isUs ? US_FEATURES : FEATURES;
  const biz = brand.business;
  const phoneHref = `tel:${(biz.phone_intl || biz.phone || "").replace(/\s/g, "")}`;
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const embedded = searchParams.get("embed") === "1" || location.pathname.includes("/embed");
  const rootRef = useRef(null);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [addressOptions, setAddressOptions] = useState([]);
  const [addressLoading, setAddressLoading] = useState(false);
  const [manualAddress, setManualAddress] = useState(false);
  const [errors, setErrors] = useState({});

  // The three address fields are one control as far as the seller is
  // concerned, whichever way they filled it in.
  const errorKey = (k) => (["house_id", "street", "selected_address_display"].includes(k) ? "address" : k);
  const clearError = (k) =>
    setErrors((e) => {
      const key = errorKey(k);
      if (!(key in e)) return e;
      const next = { ...e };
      delete next[key];
      return next;
    });

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    clearError(k);
  };
  const toggleFeature = (f) =>
    setForm((s) => ({
      ...s,
      features: s.features.includes(f) ? s.features.filter((x) => x !== f) : [...s.features, f],
    }));

  // Improvements are a list the seller builds themselves. Every row starts
  // blank apart from its type, and an empty list submits exactly as before —
  // nothing here is required to get a valuation.
  const addImprovement = () => {
    setForm((s) => (s.improvements.length >= MAX_IMPROVEMENTS ? s : {
      ...s,
      improvements: [...s.improvements, { type: "Extension", year: "", space_added_sqft: "", amount_spent: "" }],
    }));
    clearError("improvements");
  };
  const setImprovement = (i, k, v) => {
    setForm((s) => ({
      ...s,
      improvements: s.improvements.map((imp, n) => {
        if (n !== i) return imp;
        const next = { ...imp, [k]: v };
        // Switching e.g. Extension → New kitchen hides the sq ft box, but the
        // number the seller already typed would otherwise still be submitted —
        // and come back as "this kind of work doesn't add floor area" against a
        // field they can no longer see. Drop it with the box.
        if (k === "type" && !SPACE_ADDING_TYPES.includes(v)) next.space_added_sqft = "";
        return next;
      }),
    }));
    clearError("improvements");
  };
  const removeImprovement = (i) => {
    setForm((s) => ({ ...s, improvements: s.improvements.filter((_, n) => n !== i) }));
    clearError("improvements");
  };

  // The module-level defaults are UK ("Detached", "Freehold"). For a US
  // tenant, normalise the property type to a valid US option so the Select
  // isn't left on a UK value that doesn't exist in its list.
  useEffect(() => {
    if (!isUs) return;
    setForm((f) => (
      US_PROPERTY_TYPES.includes(f.property_type)
        ? f
        : { ...f, property_type: "Single-family" }
    ));
  }, [isUs]);

  // Auto-load Land Registry addresses when the user types a full postcode
  useEffect(() => {
    const pc = form.postcode.trim();
    if (pc.length < 5) {
      setAddressOptions([]);
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      setAddressLoading(true);
      try {
        const { data } = await api.get(`/postcode/${encodeURIComponent(pc)}/addresses`);
        if (!cancelled) setAddressOptions(data.properties || []);
      } catch {
        if (!cancelled) setAddressOptions([]);
      } finally {
        if (!cancelled) setAddressLoading(false);
      }
    }, 400);
    return () => { cancelled = true; clearTimeout(t); };
  }, [form.postcode]);

  const pickAddress = (opt) => {
    setForm((f) => ({
      ...f,
      house_id: opt.saon ? `${opt.saon}, ${opt.paon}` : opt.paon,
      street: opt.street.replace(/\b\w/g, (c) => c.toUpperCase()),
      selected_address_display: opt.display,
    }));
    setManualAddress(false);
    clearError("address");
  };

  // Validation is deliberately non-blocking: Continue stays live and we say
  // what's missing on click. A greyed-out button tells a seller nothing —
  // least of all that they typed a postcode but never picked an address.
  const step1Errors = () => {
    const e = {};
    if (form.postcode.trim().length < 5) e.postcode = isUs ? "Enter your ZIP code." : "Enter your full postcode.";
    if (!form.house_id.trim() || !form.street.trim())
      e.address = manualAddress || addressOptions.length === 0
        ? "Add your house name or number and your street."
        : "Choose your address from the list, or enter it manually.";
    if (!form.property_type) e.property_type = "Choose a property type.";
    if (!(Number(form.bedrooms) >= 0)) e.bedrooms = "Enter a number of bedrooms.";
    if (!(Number(form.bathrooms) >= 0)) e.bathrooms = "Enter a number of bathrooms.";
    if (!(Number(form.size_sqft) >= 100)) e.size_sqft = "Enter an approximate size of at least 100 sq ft.";
    // Only rows the seller actually added are checked — the whole section is
    // optional, but a row with no year can't be dated against the last sale.
    const thisYear = new Date().getFullYear();
    if (form.improvements.some((i) => !(Number(i.year) >= 1900 && Number(i.year) <= thisYear)))
      e.improvements = `Add the year each improvement was completed (1900–${thisYear}), or remove the row.`;
    return e;
  };

  const step2Errors = () => {
    const e = {};
    if (!form.full_name.trim()) e.full_name = "Enter your full name.";
    if (!/.+@.+\..+/.test(form.email)) e.email = "Enter a valid email address. That's where your report goes.";
    if (form.phone.trim().length < 7) e.phone = "Enter a phone number we can reach you on.";
    if (!form.consent) e.consent = `Please confirm ${biz.name || "we"} may contact you about this valuation.`;
    return e;
  };

  // Errors are built in field order, so the first key is the first problem
  // on the page.
  const showErrors = (e) => {
    setErrors(e);
    const el = document.getElementById(FIELD_IDS[Object.keys(e)[0]]);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.focus({ preventScroll: true });
  };

  // Each step replaces the panel in place, so without this the browser keeps
  // the previous scroll position and the next step — the report especially —
  // lands below the fold.
  const goToStep = (n) => {
    setErrors({});
    setStep(n);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const continueToStep2 = () => {
    const e = step1Errors();
    if (Object.keys(e).length) return showErrors(e);
    goToStep(2);
  };

  const errCls = (k) => (errors[k] ? "border-destructive" : "");
  const invalidProps = (k) =>
    errors[k] ? { "aria-invalid": true, "aria-describedby": `${FIELD_IDS[k]}-error` } : {};
  const hint = (k) =>
    errors[k] ? (
      <p id={`${FIELD_IDS[k]}-error`} data-testid={`error-${k}`} className="text-xs text-destructive mt-1.5">
        {errors[k]}
      </p>
    ) : null;

  const submit = async () => {
    const errs = step2Errors();
    if (Object.keys(errs).length) return showErrors(errs);
    setLoading(true);
    try {
      const payload = {
        ...form,
        bedrooms: Number(form.bedrooms),
        bathrooms: Number(form.bathrooms),
        size_sqft: Number(form.size_sqft),
        year_built: form.year_built ? Number(form.year_built) : null,
        owner_purchase_price: form.owner_purchase_price ? Number(form.owner_purchase_price) : null,
        owner_purchase_year: form.owner_purchase_year ? Number(form.owner_purchase_year) : null,
        // Blank optional boxes go up as null, not 0 — the backend treats 0
        // spend as "told us nothing", which is what an empty box means.
        improvements: form.improvements
          .filter((i) => i.type && i.year)
          .map((i) => ({
            type: i.type,
            year: Number(i.year),
            space_added_sqft: i.space_added_sqft ? Number(i.space_added_sqft) : null,
            amount_spent: i.amount_spent ? Number(i.amount_spent) : null,
          })),
        tenant_slug: brand.tenant_slug || undefined,
      };
      const { data } = await api.post("/valuations", payload);
      setResult(data);
      goToStep(3);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fmt = (n) => new Intl.NumberFormat("en-GB").format(n);

  // Iframe auto-resize: emit height to parent on any change.
  useEffect(() => {
    if (!embedded) return;
    const send = () => {
      const h = rootRef.current?.offsetHeight || document.body.scrollHeight;
      try {
        // Both names, deliberately. "wb-" is the first customer,
        // and is the name every snippet already pasted on a live agency site
        // is listening for; dropping it would stop those embeds resizing on
        // sites we cannot reach. "embed-" is what new snippets listen for.
        // Two postMessages to the parent cost nothing; a silently broken
        // embed on a customer's homepage costs a support ticket at best.
        window.parent?.postMessage({ type: "wb-valuation:height", height: h }, "*");
        window.parent?.postMessage({ type: "embed-valuation:height", height: h }, "*");
      } catch (e) { /* ignore */ }
    };
    send();
    const t = setInterval(send, 400);
    const ro = new ResizeObserver(send);
    if (rootRef.current) ro.observe(rootRef.current);
    return () => { clearInterval(t); ro.disconnect(); };
  }, [embedded, step, result, loading]);

  const containerCls = embedded
    ? "w-full px-4 sm:px-6 py-6"
    : "mx-auto max-w-4xl w-full px-6 lg:px-10 py-12 lg:py-16";

  return (
    <div ref={rootRef} className={embedded ? "bg-background" : "min-h-screen flex flex-col bg-background"}>
      {!embedded && (
        <SeoHead
          title={isUs
            ? `Free Home Valuation | ${biz.name || "Foundation"}`
            : `Free Instant Property Valuation | ${biz.name || "Foundation"}`}
          description={isUs
            ? `Get a free home valuation from ${biz.name || "Foundation"}. A local agent prepares a full comparative market analysis (CMA) backed by recent local sales.`
            : `Get a free, instant online valuation for your home, backed by real HM Land Registry sold prices and EPC data, from ${biz.name || "Foundation"}.`}
        />
      )}
      {!embedded && <SiteHeader />}

      <div className={containerCls}>
        {!embedded && (
          <div className="mb-8">
            <div className="text-xs uppercase tracking-[0.28em] text-muted-foreground">{isUs ? "Free home valuation" : "Instant online valuation"}</div>
            <h1 className="mt-3 font-serif-display text-4xl sm:text-5xl text-primary">
              Your personal {isUs ? "home" : "property"} valuation
            </h1>
          </div>
        )}

        {/* Stepper */}
        <div data-testid="stepper" className="mb-10 flex items-center gap-3">
          {STEPS.map(({ n, label }) => (
            <React.Fragment key={n}>
              <div className="flex items-center gap-3">
                <div
                  data-testid={`step-dot-${n}`}
                  aria-current={step === n ? "step" : undefined}
                  className={`step-dot h-8 w-8 rounded-full flex items-center justify-center text-xs border ${
                    step >= n ? "bg-primary text-primary-foreground border-primary" : "bg-background text-muted-foreground border-border"
                  }`}
                >
                  {n}
                </div>
                {/* On a phone only the step you're on is named, so three
                    labels never wrap the rail. */}
                <div
                  className={`text-xs uppercase tracking-[0.24em] whitespace-nowrap ${
                    step >= n ? "text-primary" : "text-muted-foreground"
                  } ${step === n ? "" : "hidden sm:block"}`}
                >
                  {label}
                </div>
              </div>
              {n < 3 && <div className={`h-px flex-1 ${step > n ? "bg-primary" : "bg-border"}`} />}
            </React.Fragment>
          ))}
        </div>

        {step === 1 && (
          <div data-testid="step-1-panel" className="fade-in border border-border bg-card rounded-sm p-6 lg:p-10">
            <h2 className="font-serif-display text-2xl text-primary">About the property</h2>
            <p className="text-sm text-muted-foreground mt-1">
              {isUs
                ? "Tell us about your home and a local agent will prepare a full comparative market analysis (CMA)."
                : "We'll pull your property's own Land Registry sales history to build the tightest possible valuation."}
            </p>

            <div className="mt-8 grid gap-6 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">I'd like a valuation for</Label>
                <div className="mt-3 grid grid-cols-2 gap-3">
                  {[
                    { v: "sale", label: "Sale" },
                    { v: "letting", label: isUs ? "Rental" : "Letting" },
                  ].map((o) => (
                    <button
                      key={o.v}
                      type="button"
                      data-testid={`intent-${o.v}`}
                      onClick={() => set("intent", o.v)}
                      className={`h-12 rounded-sm border transition-colors duration-300 ${
                        form.intent === o.v
                          ? "border-primary bg-primary text-primary-foreground"
                          : "border-border bg-background hover:bg-secondary/30"
                      }`}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Address block — postcode → address dropdown */}
              <div className="sm:col-span-2 border-t border-border pt-6">
                <div className="text-xs uppercase tracking-[0.28em] text-muted-foreground mb-4">Your address</div>

                <div className="grid gap-4 sm:grid-cols-3">
                  <div>
                    <Label htmlFor="postcode">{isUs ? "ZIP code" : "Postcode"}</Label>
                    <Input
                      id="postcode"
                      data-testid="input-postcode"
                      className={errCls("postcode")}
                      {...invalidProps("postcode")}
                      placeholder={isUs ? "e.g. 78704" : "e.g. GU20 6AB"}
                      value={form.postcode}
                      onChange={(e) => {
                        set("postcode", e.target.value.toUpperCase());
                        set("house_id", "");
                        set("street", "");
                        setManualAddress(false);
                      }}
                    />
                    {hint("postcode")}
                  </div>
                  <div className="sm:col-span-2">
                    <Label htmlFor="address">Address</Label>
                    {manualAddress || (form.postcode.trim().length >= 5 && !addressLoading && addressOptions.length === 0) ? (
                      <div className="grid grid-cols-3 gap-2">
                        <Input
                          id="address"
                          data-testid="input-house-id"
                          className={errCls("address")}
                          {...invalidProps("address")}
                          placeholder="House name / number"
                          value={form.house_id}
                          onChange={(e) => set("house_id", e.target.value)}
                        />
                        <Input
                          data-testid="input-street"
                          className={`col-span-2 ${errCls("address")}`}
                          placeholder="Street"
                          value={form.street}
                          onChange={(e) => set("street", e.target.value)}
                        />
                      </div>
                    ) : (
                      <Select
                        value={form.selected_address_display || ""}
                        onValueChange={(v) => {
                          const opt = addressOptions.find((o) => o.display === v);
                          if (opt) pickAddress(opt);
                        }}
                      >
                        <SelectTrigger
                          id="address"
                          data-testid="select-address"
                          className={errCls("address")}
                          {...invalidProps("address")}
                          disabled={addressLoading || addressOptions.length === 0}
                        >
                          <SelectValue placeholder={
                            addressLoading
                              ? "Loading addresses…"
                              : form.postcode.trim().length < 5
                                ? (isUs ? "Enter your ZIP code first" : "Enter your postcode first")
                                : addressOptions.length === 0
                                  ? (isUs ? "Enter your address manually below" : "No known properties for this postcode")
                                  : "Select your address"
                          } />
                        </SelectTrigger>
                        <SelectContent>
                          {addressOptions.map((o) => (
                            <SelectItem key={o.display} value={o.display} data-testid={`addr-${o.paon}`}>
                              {o.display}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    {hint("address")}
                    <button
                      type="button"
                      data-testid="toggle-manual-address"
                      onClick={() => setManualAddress((m) => !m)}
                      className="text-xs text-muted-foreground hover:text-primary transition-colors mt-2"
                    >
                      {manualAddress ? "← Use address lookup" : "Can't find your address? Enter it manually →"}
                    </button>
                  </div>
                </div>
              </div>

              {/* Property details */}
              <div className="sm:col-span-2 border-t border-border pt-6">
                <div className="text-xs uppercase tracking-[0.28em] text-muted-foreground mb-4">Property details</div>
              </div>

              <div>
                <Label htmlFor="property-type">Property type</Label>
                <Select value={form.property_type} onValueChange={(v) => set("property_type", v)}>
                  <SelectTrigger id="property-type" data-testid="select-property-type" className={errCls("property_type")} {...invalidProps("property_type")}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {propertyTypes.map((t) => <SelectItem key={t} value={t} data-testid={`ptype-${t}`}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
                {hint("property_type")}
              </div>

              <div>
                <Label>Condition</Label>
                <Select value={form.condition} onValueChange={(v) => set("condition", v)}>
                  <SelectTrigger data-testid="select-condition"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CONDITIONS.map((c) => <SelectItem key={c} value={c} data-testid={`cond-${c}`}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="bedrooms">Bedrooms</Label>
                <Input id="bedrooms" data-testid="input-bedrooms" type="number" min="0" max="15" className={errCls("bedrooms")} {...invalidProps("bedrooms")} value={form.bedrooms} onChange={(e) => set("bedrooms", e.target.value)} />
                {hint("bedrooms")}
              </div>

              <div>
                <Label htmlFor="bathrooms">Bathrooms</Label>
                <Input id="bathrooms" data-testid="input-bathrooms" type="number" min="0" max="10" className={errCls("bathrooms")} {...invalidProps("bathrooms")} value={form.bathrooms} onChange={(e) => set("bathrooms", e.target.value)} />
                {hint("bathrooms")}
              </div>

              <div>
                <Label htmlFor="size">Approx. size (sq ft)</Label>
                <Input id="size" data-testid="input-size" type="number" min="100" max="20000" className={errCls("size_sqft")} {...invalidProps("size_sqft")} value={form.size_sqft} onChange={(e) => set("size_sqft", e.target.value)} />
                {hint("size_sqft")}
              </div>

              <div>
                <Label htmlFor="year">Year built (optional)</Label>
                <Input id="year" data-testid="input-year" type="number" placeholder="e.g. 1998" value={form.year_built} onChange={(e) => set("year_built", e.target.value)} />
              </div>

              {!isUs && (
                <div className="sm:col-span-2">
                  <Label>Tenure</Label>
                  <Select value={form.tenure} onValueChange={(v) => set("tenure", v)}>
                    <SelectTrigger data-testid="select-tenure"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TENURES.map((t) => <SelectItem key={t} value={t} data-testid={`tenure-${t}`}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="sm:col-span-2">
                <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Features</Label>
                <div className="mt-3 flex flex-wrap gap-2">
                  {features.map((f) => (
                    <button
                      type="button"
                      key={f}
                      data-testid={`feature-${f}`}
                      onClick={() => toggleFeature(f)}
                      className={`px-4 py-2 text-sm rounded-full border transition-colors duration-300 ${
                        form.features.includes(f)
                          ? "border-primary bg-primary text-primary-foreground"
                          : "border-border bg-background hover:bg-secondary/30"
                      }`}
                    >
                      {f}
                    </button>
                  ))}
                </div>
              </div>

              {/* Optional improvements. When the estimate is anchored on the
                  property's own last sale it values the house as it stood on
                  that day — an extension built since is invisible and the
                  number comes out low. Entirely optional: leave it alone and
                  the valuation runs exactly as it always has. */}
              <div
                id="improvements"
                tabIndex={-1}
                data-testid="improvements-section"
                className="sm:col-span-2 border border-dashed border-border rounded-sm p-5 bg-secondary/10 outline-none"
              >
                <div className="text-xs uppercase tracking-[0.24em] text-accent">
                  Have you improved the property? (optional)
                </div>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                  If your home last changed hands years ago, the sold price on
                  record is for the house as it was then. Tell us what you've
                  done since (an extension, a loft, a new kitchen) and we'll
                  value the work on top, at what it's realistically worth today
                  rather than what it cost.
                </p>

                {form.improvements.length > 0 && (
                  <div className="mt-4 space-y-3">
                    {form.improvements.map((imp, i) => (
                      <div
                        key={i}
                        data-testid={`improvement-row-${i}`}
                        className="border border-border bg-background rounded-sm p-4"
                      >
                        <div className="grid gap-3 sm:grid-cols-2">
                          <div>
                            <Label>What was done</Label>
                            <Select value={imp.type} onValueChange={(v) => setImprovement(i, "type", v)}>
                              <SelectTrigger data-testid={`improvement-type-${i}`}><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {IMPROVEMENT_TYPES.map((t) => (
                                  <SelectItem key={t} value={t} data-testid={`imp-type-${t}`}>{t}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label htmlFor={`improvement-year-${i}`}>Year completed</Label>
                            <Input
                              id={`improvement-year-${i}`}
                              data-testid={`improvement-year-${i}`}
                              type="number"
                              min="1900"
                              max={new Date().getFullYear()}
                              className={errCls("improvements")}
                              placeholder="e.g. 2019"
                              value={imp.year}
                              onChange={(e) => setImprovement(i, "year", e.target.value)}
                            />
                          </div>
                          {SPACE_ADDING_TYPES.includes(imp.type) && (
                            <div>
                              <Label htmlFor={`improvement-sqft-${i}`}>Space added (sq ft, optional)</Label>
                              <Input
                                id={`improvement-sqft-${i}`}
                                data-testid={`improvement-sqft-${i}`}
                                type="number"
                                min="0"
                                max="5000"
                                placeholder="e.g. 250"
                                value={imp.space_added_sqft}
                                onChange={(e) => setImprovement(i, "space_added_sqft", e.target.value)}
                              />
                            </div>
                          )}
                          <div>
                            <Label htmlFor={`improvement-spend-${i}`}>Roughly what it cost ({cur}, optional)</Label>
                            <Input
                              id={`improvement-spend-${i}`}
                              data-testid={`improvement-spend-${i}`}
                              type="number"
                              min="0"
                              placeholder="e.g. 40000"
                              value={imp.amount_spent}
                              onChange={(e) => setImprovement(i, "amount_spent", e.target.value)}
                            />
                          </div>
                        </div>
                        <button
                          type="button"
                          data-testid={`improvement-remove-${i}`}
                          onClick={() => removeImprovement(i)}
                          className="text-xs text-muted-foreground hover:text-primary transition-colors mt-3"
                        >
                          Remove this improvement
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {hint("improvements")}

                {form.improvements.length < MAX_IMPROVEMENTS && (
                  <button
                    type="button"
                    data-testid="improvement-add"
                    onClick={addImprovement}
                    className="mt-4 px-4 py-2 text-sm rounded-full border border-border bg-background hover:bg-secondary/30 transition-colors duration-300"
                  >
                    {form.improvements.length === 0 ? "+ Add an improvement" : "+ Add another"}
                  </button>
                )}
                <p className="text-xs text-muted-foreground mt-3 leading-relaxed">
                  Nobody gets their money back pound for pound, so we don't
                  pretend they do. Spend counts partially and fades as the work
                  ages, while added floor area is valued at the going rate per
                  sq ft in your street.
                </p>
              </div>

              {/* Optional owner-declared purchase — critical for pre-1995 homes.
                  HM Land Registry only holds records from Jan 1995 onwards, so
                  properties last sold before then have no digital history to
                  anchor against. When the owner tells us what they paid + when,
                  we time-adjust that figure using the UK long-run HPI to give
                  the same accuracy as a Land Registry anchor. */}
              {!isUs && (
              <div className="sm:col-span-2 border border-dashed border-border rounded-sm p-5 bg-secondary/10">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-xs uppercase tracking-[0.24em] text-accent">
                      Bought before 1995? (optional)
                    </div>
                    <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                      HM Land Registry only holds sales from January 1995 onwards.
                      If your property last changed hands before then, tell us
                      what you paid and when. We'll time-adjust it for UK
                      house-price growth to build a far more accurate estimate
                      than nearby comparables alone.
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 mt-4">
                  <div>
                    <Label htmlFor="owner-price">Purchase price ({cur})</Label>
                    <Input
                      id="owner-price"
                      data-testid="input-owner-purchase-price"
                      type="number"
                      min="1000"
                      placeholder="e.g. 120000"
                      value={form.owner_purchase_price}
                      onChange={(e) => set("owner_purchase_price", e.target.value)}
                    />
                  </div>
                  <div>
                    <Label htmlFor="owner-year">Purchase year</Label>
                    <Input
                      id="owner-year"
                      data-testid="input-owner-purchase-year"
                      type="number"
                      min="1900"
                      max="2100"
                      placeholder="e.g. 1994"
                      value={form.owner_purchase_year}
                      onChange={(e) => set("owner_purchase_year", e.target.value)}
                    />
                  </div>
                </div>
              </div>
              )}
            </div>

            <div className="mt-10 flex justify-end">
              <Button
                data-testid="step1-next"
                onClick={continueToStep2}
                className="h-11 px-6 rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 transition-colors duration-300"
              >
                Continue <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div data-testid="step-2-panel" className="fade-in border border-border bg-card rounded-sm p-6 lg:p-10">
            <h2 className="font-serif-display text-2xl text-primary">Where should we send your report?</h2>
            <p className="text-sm text-muted-foreground mt-1">
              {isUs
                ? "A branded home valuation with recent comparable sales in your area, plus a local agent to follow up with a full CMA."
                : "A branded PDF valuation with your property's own Land Registry history and recent nearby sales, delivered instantly."}
            </p>

            {form.selected_address_display || (form.house_id && form.street) ? (
              <div className="mt-6 border-l-2 border-accent pl-4 py-1 text-sm">
                <div className="text-xs uppercase tracking-[0.24em] text-muted-foreground">Valuing</div>
                <div className="text-primary mt-1">{form.selected_address_display || `${form.house_id} ${form.street}`}, {form.postcode}</div>
              </div>
            ) : null}

            <div className="mt-8 grid gap-6 sm:grid-cols-2">
              <div>
                <Label htmlFor="name">Full name</Label>
                <Input id="name" data-testid="input-name" className={errCls("full_name")} {...invalidProps("full_name")} value={form.full_name} onChange={(e) => set("full_name", e.target.value)} />
                {hint("full_name")}
              </div>
              <div>
                <Label htmlFor="email">Email address</Label>
                <Input id="email" data-testid="input-email" type="email" className={errCls("email")} {...invalidProps("email")} value={form.email} onChange={(e) => set("email", e.target.value)} />
                {hint("email")}
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="phone">Phone number</Label>
                <Input id="phone" data-testid="input-phone" className={errCls("phone")} {...invalidProps("phone")} value={form.phone} onChange={(e) => set("phone", e.target.value)} />
                {hint("phone")}
              </div>

              <div className="sm:col-span-2 pt-2">
                <div className="flex items-start gap-3">
                  <Checkbox id="consent" data-testid="input-consent" className={errCls("consent")} {...invalidProps("consent")} checked={form.consent} onCheckedChange={(v) => set("consent", !!v)} />
                  <Label htmlFor="consent" className="text-sm text-muted-foreground leading-relaxed">
                    I agree that {biz.name} may contact me about this valuation. See our{brand.legal?.privacy ? (
                      <> <Link to={tenantPath("privacy")} className="underline underline-offset-2">privacy policy</Link>.</>
                    ) : ` privacy policy${biz.website_url ? ` on ${biz.website_url.replace(/^https?:\/\//, "")}` : ""}.`}
                  </Label>
                </div>
                {hint("consent")}
              </div>
            </div>

            <div className="mt-10 flex justify-between">
              <Button variant="outline" data-testid="step2-back" onClick={() => goToStep(1)} className="h-11 px-5 rounded-sm">
                <ArrowLeft className="mr-2 w-4 h-4" /> Back
              </Button>
              <Button
                data-testid="step2-submit"
                disabled={loading}
                onClick={submit}
                className="h-11 px-6 rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 transition-colors duration-300"
              >
                {loading ? (<><Loader2 className="mr-2 w-4 h-4 animate-spin" /> Preparing report…</>) : (<>See my valuation <ArrowRight className="ml-2 w-4 h-4" /></>)}
              </Button>
            </div>
          </div>
        )}

        {step === 3 && result && (
          <div data-testid="step-3-panel" className="fade-in space-y-8">
            <div className="border border-border bg-card rounded-sm p-6 lg:p-10">
              <div className="flex items-center gap-2 text-accent text-sm">
                <CheckCircle2 className="w-4 h-4" />
                {result.estimated === false
                  ? `Request received, ${biz.name || "your agent"} is on it`
                  : "Your indicative valuation"}
              </div>

              {/* NO ESTIMATE IS A REAL ANSWER AND IT RENDERS AS ONE.
                  A US brokerage with no MLS feed, or too few recent closed
                  sales near the address, gets no figure — and the three
                  numbers below would draw "$0" in 60px type on the agent's
                  own website. The server says so with a flag rather than
                  with zeroes; see us_valuation.estimate, which returns None
                  rather than a number it cannot stand behind. */}
              {result.estimated === false ? (
                <div className="mt-4" data-testid="result-cma">
                  <div className="text-xs uppercase tracking-[0.28em] text-muted-foreground">
                    A proper valuation, not an algorithm
                  </div>
                  <p className="mt-3 text-[15px] leading-relaxed text-muted-foreground max-w-prose">
                    {result.commentary}
                  </p>
                </div>
              ) : (
              <div className="mt-4">
                <div className="text-xs uppercase tracking-[0.28em] text-muted-foreground">
                  {form.intent === "sale" ? "Estimated market value" : "Estimated monthly rent"}
                </div>
                <div data-testid="result-mid" className="mt-2 font-serif-display text-5xl lg:text-6xl text-primary">
                  {cur}{fmt(result.estimate_mid)}{form.intent === "letting" ? rentSuffix : ""}
                </div>
                <div className="mt-3 text-sm text-muted-foreground">
                  Indicative range:&nbsp;
                  <span data-testid="result-low" className="text-primary font-medium">{cur}{fmt(result.estimate_low)}</span>
                  &nbsp;–&nbsp;
                  <span data-testid="result-high" className="text-primary font-medium">{cur}{fmt(result.estimate_high)}</span>
                  {form.intent === "letting" && rentSuffix}
                </div>

                {isUs && (
                  <div className="mt-6 border-l-2 border-accent pl-4 py-2">
                    <div className="text-xs uppercase tracking-[0.24em] text-accent">Your full CMA is on its way</div>
                    <div className="text-xs text-muted-foreground mt-2 leading-relaxed">
                      This is an automated estimate. A local {biz.name || "agent"} will prepare a full comparative market analysis (CMA) reflecting your home's condition, recent upgrades and live comparable sales, then follow up with you directly.
                    </div>
                  </div>
                )}

                {!isUs && result.methodology?.primary_signal === "property-history" && result.property_history?.length > 0 && (
                  <div data-testid="anchor-badge" className="mt-6 border-l-2 border-accent pl-4 py-2">
                    <div className="text-xs uppercase tracking-[0.24em] text-accent">Anchored on your property's own sales history</div>
                    <div className="mt-2 space-y-1 text-sm text-primary">
                      {result.property_history.slice(0, 3).map((h, i) => (
                        <div key={i} className="flex justify-between max-w-md">
                          <span className="text-muted-foreground">{h.date?.slice(0, 16)}</span>
                          <span className="font-medium">£{fmt(h.price)}</span>
                        </div>
                      ))}
                    </div>
                    <div className="text-xs text-muted-foreground mt-2">
                      Sourced directly from HM Land Registry, a far stronger signal than neighbourhood averages.
                    </div>
                  </div>
                )}

                {!isUs && result.methodology?.primary_signal === "owner-declared" && (
                  <div data-testid="anchor-badge" className="mt-6 border-l-2 border-accent pl-4 py-2">
                    <div className="text-xs uppercase tracking-[0.24em] text-accent">Anchored on your reported purchase</div>
                    <div className="mt-2 text-sm text-primary flex justify-between max-w-md">
                      <span className="text-muted-foreground">Purchased {form.owner_purchase_year}</span>
                      <span className="font-medium">£{fmt(Number(form.owner_purchase_price))}</span>
                    </div>
                    <div className="text-xs text-muted-foreground mt-2">
                      Time-adjusted to today using the long-run UK house-price index. Land Registry doesn't hold sales prior to Jan 1995, so this owner-reported figure gives us a far tighter anchor than neighbourhood averages alone.
                    </div>
                  </div>
                )}

                {!isUs && result.methodology?.primary_signal === "comparables" && (
                  <div data-testid="anchor-badge" className="mt-6 border-l-2 border-border pl-4 py-2 text-xs text-muted-foreground">
                    We couldn't find prior HM Land Registry sales at this exact address, so this estimate is based on {result.methodology?.comparable_count || 0} recent nearby comparables within ~0.5 mile.{" "}
                    <span className="text-primary">Bought before 1995? Re-run the report and add your purchase details for a much tighter estimate.</span>
                  </div>
                )}
              </div>
              )}

              {/* The commentary carries the whole message when there is no
                  figure, so it is not repeated above it. */}
              {result.estimated !== false && (
                <div className="mt-8 border-t border-border pt-6">
                  <div className="text-xs uppercase tracking-[0.28em] text-muted-foreground mb-3">Market commentary</div>
                  <p data-testid="result-commentary" className="text-primary leading-relaxed">{result.commentary}</p>
                </div>
              )}
            </div>

            {/* How we got to this figure — transparent calculation breakdown.
                Builds trust with sellers and gives the agent a talking-point
                on the follow-up call. UK-only: it's built on HM Land Registry
                methodology, which has no US equivalent in this build. */}
            {!isUs && result.methodology && (
              <div data-testid="calculation-breakdown" className="border border-border bg-card rounded-sm p-6 lg:p-10">
                <div className="text-xs uppercase tracking-[0.28em] text-muted-foreground mb-6">How we got to this figure</div>
                {(() => {
                  const m = result.methodology;
                  const src = m.primary_signal;
                  const base = m.base || m.property_anchor || m.anchor || 0;
                  const modPct = ((m.modifier || 1) - 1) * 100;
                  const sizePct = ((m.size_factor || 1) - 1) * 100;
                  const spreadPct = (m.spread || 0) * 100;

                  const anchorLabel =
                    src === "property-history" ? "HM Land Registry, this property's own last sale" :
                    src === "owner-declared"   ? `Your reported purchase (£${fmt(Number(form.owner_purchase_price || 0))} in ${form.owner_purchase_year || ""})` :
                                                 `Trimmed median of ${m.comparable_count || 0} nearby comparables`;

                  // Name the index that actually did the work. This used to
                  // say "~4.5% p.a." — one flat rate for every year and every
                  // region, which is the thing the HPI series replaced.
                  const hpiRegion = m.hpi?.region_label || "";
                  const hpiWhere =
                    !hpiRegion || hpiRegion === "United Kingdom" ? "the UK"
                    : /South|North|East|West|Yorkshire/.test(hpiRegion) ? `the ${hpiRegion}`
                    : hpiRegion;
                  const hpiDetail = `carried forward on HM Land Registry's House Price Index for ${hpiWhere} (a monthly index of what homes here actually did, not a flat growth rate)`;

                  const anchorDetail =
                    src === "property-history" && result.property_history?.[0]
                      ? `£${fmt(result.property_history[0].price)} in ${(result.property_history[0].date_iso || result.property_history[0].date || "").slice(0,4)}, ${hpiDetail}.`
                      : src === "owner-declared"
                      ? `Your figure ${hpiDetail}.`
                      : `Within ~0.5 mile of your postcode, same property type where possible.`;

                  const imp = m.improvements;
                  const impCounted = imp?.counted || [];

                  // What's actually driving the width of the band, in the
                  // seller's words. An honest range needs an honest reason.
                  const bandWhy = [];
                  if (m.anchor_year) {
                    const age = new Date().getFullYear() - m.anchor_year;
                    if (age >= 8) bandWhy.push(`that sale is ${age} years old`);
                  }
                  if ((m.comparable_count || 0) < 8) bandWhy.push(`only ${m.comparable_count || 0} nearby sales to check it against`);
                  if ((m.dispersion_pct || 0) >= 12) bandWhy.push("nearby prices vary a lot");
                  if (src === "owner-declared") bandWhy.push("the purchase figure is your own rather than the registry's");
                  if (impCounted.length) bandWhy.push("improvements are as you've described them");

                  return (
                    <div className="space-y-6">
                      {/* Step 1 — Anchor */}
                      <div className="grid sm:grid-cols-[24px_1fr_auto] gap-4 items-start">
                        <div className="w-6 h-6 rounded-full border border-accent flex items-center justify-center text-xs text-accent font-medium">1</div>
                        <div>
                          <div className="text-sm font-medium text-primary">Anchor value</div>
                          <div className="text-xs text-muted-foreground mt-1">Source: {anchorLabel}</div>
                          <div className="text-xs text-muted-foreground mt-0.5">{anchorDetail}</div>
                        </div>
                        <div data-testid="calc-anchor" className="font-serif-display text-lg text-primary whitespace-nowrap">£{fmt(base)}</div>
                      </div>

                      {/* Step 2 — Adjustments */}
                      <div className="grid sm:grid-cols-[24px_1fr_auto] gap-4 items-start">
                        <div className="w-6 h-6 rounded-full border border-accent flex items-center justify-center text-xs text-accent font-medium">2</div>
                        <div className="space-y-1">
                          <div className="text-sm font-medium text-primary">Adjustments applied</div>
                          {src !== "comparables" ? (
                            <div className="text-xs text-muted-foreground">
                              Because we're anchored on this property's own value, we only nudge for condition changes since the last transaction, with no re-stacking of size/tenure/features.
                            </div>
                          ) : (
                            <div className="text-xs text-muted-foreground space-y-0.5">
                              <div>Size scaling ({form.size_sqft} sq ft): {sizePct >= 0 ? "+" : ""}{sizePct.toFixed(1)}%</div>
                              <div>Condition, tenure &amp; features stack: {modPct >= 0 ? "+" : ""}{modPct.toFixed(1)}%</div>
                            </div>
                          )}
                        </div>
                        <div data-testid="calc-modifier" className="font-serif-display text-lg text-primary whitespace-nowrap">×{(m.modifier * (src === "comparables" ? m.size_factor : 1)).toFixed(3)}</div>
                      </div>

                      {/* Improvements — only when the seller told us about
                          some. Marked "+" rather than numbered so the three
                          fixed steps keep their numbers. */}
                      {impCounted.length > 0 && (
                        <div data-testid="calc-improvements" className="grid sm:grid-cols-[24px_1fr_auto] gap-4 items-start">
                          <div className="w-6 h-6 rounded-full border border-accent flex items-center justify-center text-xs text-accent font-medium">+</div>
                          <div className="space-y-1">
                            <div className="text-sm font-medium text-primary">Improvements you've made</div>
                            <div className="text-xs text-muted-foreground space-y-0.5">
                              {impCounted.map((c, i) => (
                                <div key={i}>
                                  {c.type} ({c.year}), {c.basis}: +£{fmt(c.uplift)}
                                </div>
                              ))}
                              {(imp.skipped || []).map((s, i) => (
                                <div key={`s${i}`} className="opacity-70">
                                  {s.type} ({s.year}), not counted: {s.reason}
                                </div>
                              ))}
                              {imp.damp < 1 && (
                                <div>
                                  Scaled to {Math.round(imp.damp * 100)}% because this estimate leans on nearby sales, which already reflect the home as it is today.
                                </div>
                              )}
                              {imp.capped && <div>Capped at 30% of the figure above.</div>}
                            </div>
                          </div>
                          <div className="font-serif-display text-lg text-primary whitespace-nowrap">+£{fmt(m.improvements_uplift || 0)}</div>
                        </div>
                      )}

                      {/* Step 3 — Result */}
                      <div className="grid sm:grid-cols-[24px_1fr_auto] gap-4 items-start border-t border-border pt-6">
                        <div className="w-6 h-6 rounded-full bg-accent text-primary-foreground flex items-center justify-center text-xs font-medium">3</div>
                        <div>
                          <div className="text-sm font-medium text-primary">
                            {form.intent === "sale" ? "Mid estimate" : "Indicative monthly rent"}
                            <span className="text-muted-foreground font-normal"> (rounded to the nearest {form.intent === "sale" ? "£5,000" : "£25"})</span>
                          </div>
                          <div className="text-xs text-muted-foreground mt-1">
                            Confidence range: ±{spreadPct.toFixed(1)}% either side
                            {bandWhy.length > 0
                              ? `, widened because ${bandWhy.join(", ")}.`
                              : ". The evidence here is recent, plentiful and consistent."}
                          </div>
                          {typeof m.confidence === "number" && (
                            <div data-testid="calc-confidence" className="mt-3 flex items-center gap-3">
                              <div className="text-xs uppercase tracking-[0.24em] text-muted-foreground w-24">Confidence</div>
                              <div className="flex-1 h-1.5 bg-secondary/50 rounded-full overflow-hidden max-w-[220px]">
                                <div
                                  className="h-full bg-accent transition-all"
                                  style={{ width: `${Math.max(4, Math.min(100, m.confidence))}%` }}
                                />
                              </div>
                              <div className="text-xs text-primary font-medium tabular-nums">{m.confidence}%</div>
                            </div>
                          )}
                        </div>
                        <div data-testid="calc-final" className="font-serif-display text-xl text-primary whitespace-nowrap">
                          £{fmt(result.estimate_mid)}{form.intent === "letting" ? " pcm" : ""}
                        </div>
                      </div>
                    </div>
                  );
                })()}
                {result.methodology?.area_price_per_sqft?.available && (
                  <div data-testid="calc-area-sqft" className="mt-6 pt-6 border-t border-border grid sm:grid-cols-[24px_1fr_auto] gap-4 items-start">
                    <div className="w-6 h-6 rounded-full border border-border flex items-center justify-center text-xs text-muted-foreground font-medium">£</div>
                    <div>
                      <div className="text-sm font-medium text-primary">Average price per sq ft in this area</div>
                      <div className="text-xs text-muted-foreground mt-1">
                        Based on {result.methodology.area_price_per_sqft.sample_count} nearby HM Land Registry sales matched against their EPC floor-area records, a genuine local figure, not sqft × a national average.
                      </div>
                    </div>
                    <div className="font-serif-display text-lg text-primary whitespace-nowrap">
                      £{fmt(result.methodology.area_price_per_sqft.avg_price_per_sqft)}/sq ft
                    </div>
                  </div>
                )}
                <div className="mt-6 pt-4 border-t border-border text-xs text-muted-foreground leading-relaxed">
                  This estimate is indicative and generated from public HM Land Registry sold-price data. For a precise, up-to-the-minute figure that reflects the internal condition and any recent improvements, book a free in-person valuation with our team.
                </div>
              </div>
            )}

            {form.intent === "sale" && result.target_coords && (result.comparable_sales?.length || 0) > 0 && (
              <div>
                <div className="text-xs uppercase tracking-[0.28em] text-muted-foreground mb-3">
                  Recent nearby sales on the map
                </div>
                <SalesMap
                  target={{
                    latitude: result.target_coords.latitude,
                    longitude: result.target_coords.longitude,
                    label: `${form.house_id} ${form.street}, ${form.postcode}`,
                  }}
                  sales={result.comparable_sales || []}
                />
              </div>
            )}


            <div className="grid sm:grid-cols-2 gap-4">
              <a href={phoneHref} data-testid="result-call-cta" className="border border-border bg-primary text-primary-foreground rounded-sm p-6 hover:opacity-95 transition-opacity duration-300">
                <div className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-secondary" />
                  <div>
                    <div className="text-xs uppercase tracking-[0.24em] text-primary-foreground/60">Call our team</div>
                    <div className="font-serif-display text-2xl">{biz.phone}</div>
                  </div>
                </div>
              </a>
              <button
                type="button"
                data-testid="result-restart"
                onClick={() => { setForm(initial); setResult(null); goToStep(1); }}
                className="text-left border border-border bg-card rounded-sm p-6 hover:bg-secondary/30 transition-colors duration-300"
              >
                <div className="flex items-center gap-3">
                  <Home className="w-5 h-5 text-accent" />
                  <div>
                    <div className="text-xs uppercase tracking-[0.24em] text-muted-foreground">Valuing another home?</div>
                    <div className="font-serif-display text-2xl text-primary">Start again</div>
                  </div>
                </div>
              </button>
            </div>

            <p className="text-xs text-muted-foreground max-w-2xl">
              This is a computer-generated estimate based on the details supplied and prevailing market conditions. For a precise figure we recommend an in-person valuation with one of our local experts.
            </p>
          </div>
        )}
      </div>

      {!embedded && <SiteFooter />}
    </div>
  );
}
